import React from 'react';

import { bemBlock } from '@neptune/shared/venus-ui';

import { Comparison } from '../text-diff/Comparison';
import { diffStrings, Token } from '../text-diff/diff';

import { getTokensCharsLength } from './getTokensCharsLength';
import { sliceTokensByChars } from './sliceTokensByChars';

import './MultiLineDiffEllipsis.less';

const block = bemBlock('multiline-diff-ellipsis');

type MultiLineDiffMiddleEllipsisProps = {
  value: string;
  referenceValue: string;
  limit?: number;
  onTruncationChange?: (isTruncated: boolean) => void;
};

export const MultiLineDiffEllipsis = React.forwardRef<
  HTMLSpanElement,
  MultiLineDiffMiddleEllipsisProps
>(({ value, referenceValue, limit = 500, onTruncationChange }, ref) => {
  const revealedRef = React.useRef<HTMLSpanElement>(null);

  const tokens = React.useMemo<Token[]>(
    // limit + 1 to get the true information whether is truncated or not
    () => diffStrings(value.substring(0, limit + 1), referenceValue.substring(0, limit + 1)),
    [value, referenceValue, limit],
  );

  const isTruncated = limit < getTokensCharsLength(tokens);

  React.useEffect(() => {
    onTruncationChange?.(isTruncated);
  }, [isTruncated, onTruncationChange]);

  const firstTokensSliced = sliceTokensByChars(tokens, limit);

  const handleCopy = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.clipboardData.setData('text/plain', value);
  };

  return (
    <span
      ref={ref}
      data-role="text-diff"
      onCopy={handleCopy}
      className={block({ modifiers: { truncated: isTruncated } })}
      title={value}
    >
      {isTruncated ? (
        <>
          <span ref={revealedRef} className={block('text')}>
            <Comparison tokens={firstTokensSliced} />
          </span>
          <span>...</span>
        </>
      ) : (
        <Comparison tokens={tokens} />
      )}
    </span>
  );
});

MultiLineDiffEllipsis.displayName = 'MultiLineDiffMiddleEllipsis';
